import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { useDateContext } from "@/components/providers/date-provider";
import { DataTableColumnHeader } from "@/components/ui/data-table/column-header";

export interface CreatedAtCellProps {
	value?: string | null;
}

/**
 * Renders a record's creation timestamp as a date with the time beneath it.
 *
 * Formatting goes through the date context so the value is rendered in the
 * portal's timezone rather than whatever timezone the admin's browser happens
 * to be set to — an evening timestamp would otherwise land on the wrong
 * calendar day — and honours the user's locale and 12/24-hour preference.
 */
export const CreatedAtCell = ({ value }: CreatedAtCellProps) => {
	const { locale, tzDate, timeFormatDateFns } = useDateContext();

	const date = value ? new Date(value) : null;
	if (!date || Number.isNaN(date.getTime())) {
		return <span className="text-muted-foreground/75">N/A</span>;
	}

	return (
		<div className="text-nowrap text-muted-foreground/75">
			<p>{format(date, "PP", { locale, in: tzDate })}</p>
			<p className="text-xs">
				{format(date, timeFormatDateFns, { locale, in: tzDate })}
			</p>
		</div>
	);
};

/**
 * Column definition for a record's creation timestamp.
 *
 * Sorting is off on purpose: these tables are paginated server-side (pagy) but
 * the table sorts client-side, so an enabled header would silently sort only
 * the page on screen and pass it off as sorting the whole list.
 */
export function createdAtColumn<TData extends { createdAt?: string | null }>(
	title: string,
): ColumnDef<TData> {
	return {
		accessorKey: "createdAt",
		enableSorting: false,
		enableHiding: false,
		header: ({ column }) => (
			<DataTableColumnHeader column={column} title={title} />
		),
		cell: ({ row }) => <CreatedAtCell value={row.original.createdAt} />,
	};
}
